import React, { useEffect, useRef, useState } from "react";
import { Box, Card, Typography, CardContent, Grid, Backdrop, Divider } from "@material-ui/core";
import { FormikProps, useFormik } from "formik";
import moment from "moment";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import TabHeader from "src/components/TabHeader/TabHeader";
import CommonButton from "src/components/CommonButton/CommonButton";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonLoader from "src/components/Loader/CommonLoader";
import { useAppSelector } from "src/app-redux/hooks";
import {
  selectAllAgent,
  selectAllPartyNameRowsList,
} from "src/app-redux/configuration/configurationSlice";
import {
  getAllAgents,
  getAllPartName,
} from "src/app-redux/configuration/actions/configurationActions";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { useStyles } from "./style";

type ReportFormValues = {
  entityId: number | "";
  startDate: string;
  endDate: string;
};

const defaultDates = {
  startDate: moment(new Date().setHours(0, 0, 0, 0)).format("YYYY-MM-DDTHH:mm"),
  endDate: moment().format("YYYY-MM-DDTHH:mm"),
};

const createReportValidationSchema = () =>
  Yup.object().shape({
    entityId: Yup.number().required("This field is required"),
    startDate: Yup.string().required("This field is required"),
    endDate: Yup.string().required("This field is required"),
  });

const printStatementOfAccountReport = async (
  payload: Record<string, unknown>,
  setLoader: (value: boolean) => void
) => {
  setLoader(true);
  const idToken = localStorage.getItem("token");
  const requestOptions = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  };

  fetch(`${process.env.REACT_APP_API_URL}/get-statement_of_account-report`, requestOptions)
    .then((response) => {
      if (response.status === 200) return response.blob();
      throw response;
    })
    .then((data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const blobURL = URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      document.body.appendChild(iframe);
      iframe.style.display = "none";
      iframe.src = blobURL;
      iframe.onload = function () {
        setTimeout(function () {
          iframe.focus();
          iframe.contentWindow?.print();
        }, 1);
      };
      setLoader(false);
    })
    .catch(() => {
      setLoader(false);
    });
};

type ReportFormRowProps = {
  entityLabel: string;
  entityPlaceholder: string;
  options: any[];
  formik: FormikProps<ReportFormValues>;
  startDatePickerRef: React.MutableRefObject<any>;
  endDatePickerRef: React.MutableRefObject<any>;
  classes: ReturnType<typeof useStyles>;
};

function ReportFormRow({
  entityLabel,
  entityPlaceholder,
  options,
  formik,
  startDatePickerRef,
  endDatePickerRef,
  classes,
}: ReportFormRowProps): React.ReactElement {
  return (
    <Grid
      item
      xs={12}
      className={classes.outer}
      container
      direction="row"
      spacing={2}
      alignItems="flex-start"
    >
      <Grid item xs={12} md={4}>
        <Box className={classes.fieldBox}>
          <CommonAutoCompleteField
            cssClassName="customTextField"
            label={entityLabel}
            placeholder={entityPlaceholder}
            name="entityId"
            optionsType={options}
            getOptionLabel={(option: any) => option.name || ""}
            value={
              options.find((item: any) => String(item.id) === String(formik.values.entityId)) ||
              null
            }
            onChange={(option: any) => {
              formik.setFieldValue("entityId", option?.id ?? "");
            }}
            error={formik.touched.entityId && Boolean(formik.errors.entityId)}
            helperText={formik.touched.entityId && formik.errors.entityId}
            required
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={3}>
        <Box className={classes.fieldBox}>
          <CommonTextFieldDate
            label="Start Date"
            name="startDate"
            cssClassName="customTextField"
            value={formik.values.startDate}
            datePickerRef={startDatePickerRef}
            onChange={(e: any) => {
              formik.setFieldValue("startDate", moment(e).format("YYYY-MM-DDTHH:mm"));
              formik.setFieldValue("endDate", "");
            }}
            error={formik.touched.startDate && Boolean(formik.errors.startDate)}
            helperText={formik.touched.startDate && formik.errors.startDate}
            maxDate={moment().format("YYYY-MM-DDTHH:mm")}
            required
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={3}>
        <Box className={classes.fieldBox}>
          <CommonTextFieldDate
            label="End Date"
            name="endDate"
            cssClassName="customTextField"
            value={formik.values.endDate}
            datePickerRef={endDatePickerRef}
            onChange={(e: any) => {
              formik.setFieldValue("endDate", moment(e).format("YYYY-MM-DDTHH:mm"));
            }}
            error={formik.touched.endDate && Boolean(formik.errors.endDate)}
            helperText={formik.touched.endDate && formik.errors.endDate}
            minDate={formik.values.startDate}
            maxDate={moment().format("YYYY-MM-DDTHH:mm")}
            required
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={2}>
        <Box className={classes.buttonBox}>
          <CommonButton isGreen cssClass={classes.printButton} onClick={() => formik.submitForm()}>
            Print Report
          </CommonButton>
        </Box>
      </Grid>
    </Grid>
  );
}

export function CommercialReports(): React.ReactElement {
  const classes = useStyles();
  const dispatch = useDispatch();
  const allPartyNameRows = useAppSelector(selectAllPartyNameRowsList);
  const allAgents = useAppSelector(selectAllAgent);
  const [loader, setLoader] = useState<boolean>(false);
  const [consigneeList, setConsigneeList] = useState<{ id: number; name: string }[]>([]);

  const forwarderStartDatePickerRef = useRef<any>(null);
  const forwarderEndDatePickerRef = useRef<any>(null);
  const consigneeStartDatePickerRef = useRef<any>(null);
  const consigneeEndDatePickerRef = useRef<any>(null);
  const agentStartDatePickerRef = useRef<any>(null);
  const agentEndDatePickerRef = useRef<any>(null);

  const moduleName = "Commercial Reports";
  const pageHeading = "Commercial Reports";

  const forwarderFormik = useFormik<ReportFormValues>({
    initialValues: {
      entityId: "",
      ...defaultDates,
    },
    validationSchema: createReportValidationSchema(),
    onSubmit: async (values) => {
      await printStatementOfAccountReport(
        {
          startDate: values.startDate,
          endDate: values.endDate,
          ForwarderId: Number(values.entityId),
        },
        setLoader
      );
    },
  });

  const consigneeFormik = useFormik<ReportFormValues>({
    initialValues: {
      entityId: "",
      ...defaultDates,
    },
    validationSchema: createReportValidationSchema(),
    onSubmit: async (values) => {
      await printStatementOfAccountReport(
        {
          startDate: values.startDate,
          endDate: values.endDate,
          ConsigneeId: Number(values.entityId),
        },
        setLoader
      );
    },
  });

  const agentFormik = useFormik<ReportFormValues>({
    initialValues: {
      entityId: "",
      ...defaultDates,
    },
    validationSchema: createReportValidationSchema(),
    onSubmit: async (values) => {
      await printStatementOfAccountReport(
        {
          startDate: values.startDate,
          endDate: values.endDate,
          agentId: Number(values.entityId),
        },
        setLoader
      );
    },
  });

  useEffect(() => {
    dispatch(getAllPartName(false));
    dispatch(getAllAgents(true));
  }, [dispatch]);

  useEffect(() => {
    const getConsigneeListing = async () => {
      try {
        const results = await fetchWrapper.get(`get_all-consignee-details?isActive=${true}`);
        if (results.isSuccess === true) {
          const raw = results.value?.consigneeList ?? results.value;
          const list = Array.isArray(raw)
            ? raw.map((item: { id: number; name?: string }) => ({
                id: item.id,
                name: item.name ?? "",
              }))
            : [];
          setConsigneeList(list);
        }
      } catch {
        // ignore
      }
    };

    getConsigneeListing();
  }, []);

  return (
    <>
      <TabHeader module={moduleName} heading={pageHeading} />
      <Box m={2} />
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid md={8}>
              <Box mt={1} mb={2}>
                <Typography className={classes.pageHeading}>{pageHeading}</Typography>
              </Box>
            </Grid>
          </Grid>
          <Divider />
          <ReportFormRow
            entityLabel="Forwarder"
            entityPlaceholder="Select Forwarder"
            options={allPartyNameRows}
            formik={forwarderFormik}
            startDatePickerRef={forwarderStartDatePickerRef}
            endDatePickerRef={forwarderEndDatePickerRef}
            classes={classes}
          />
          <Box mt={2} />
          <ReportFormRow
            entityLabel="Consignee Name"
            entityPlaceholder="Select Consignee Name"
            options={consigneeList}
            formik={consigneeFormik}
            startDatePickerRef={consigneeStartDatePickerRef}
            endDatePickerRef={consigneeEndDatePickerRef}
            classes={classes}
          />
          <Box mt={2} />
          <ReportFormRow
            entityLabel="Agent Name"
            entityPlaceholder="Select Agent Name"
            options={allAgents || []}
            formik={agentFormik}
            startDatePickerRef={agentStartDatePickerRef}
            endDatePickerRef={agentEndDatePickerRef}
            classes={classes}
          />
        </CardContent>
      </Card>
    </>
  );
}
